/**
 * voice-v8 — Werkzeugkarten: Projekte: Projekt anlegen/verknüpfen, Projektdateien, Vorlesen als MP3.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { absatz, baueKarte, kartenTitel, leerHinweis, wertZeile } from "../karte.js";
import { buildAudioPlayer } from "../audio-player.js";
import { unwrapResult } from "./hilfe.js";

export function buildProjectActionCard(tool: string, result: unknown): HTMLElement {
  // Pre.150: kompakte Inline-Card statt Badge + KV-Rows. ID rauf — der
  // User braucht sie im Chat nicht, sie steht in der Projekte-View. Nur
  // die Aktion + der Projektname als kurze, klar lesbare Zeile.
  const r = unwrapResult(result);
  // 14.10.2026: keine „compact"-Sonderkarte mehr — eine Zeile als Titel, aus dem Grundgerüst.
  const el = baueKarte("project-action");
  const verbs: Record<string, string> = {
    project_create: "angelegt",
    project_set_active: "aktiviert",
    project_link: "verknüpft",
    project_unlink: "Verknüpfung entfernt",
    project_delete: "gelöscht",
  };
  const name = typeof r.name === "string" && r.name ? r.name : "(ohne Name)";
  const verb = verbs[tool] ?? tool;

  el.appendChild(
    kartenTitel(
      tool === "project_unlink"
        ? `Verknüpfung mit „${name}“ entfernt`
        : r.activeProjectId === null && tool === "project_set_active"
          ? "Kein aktives Projekt"
          : `Projekt „${name}“ ${verb}`,
    ),
  );
  return el;
}

// --- project_file_* (Sprint A) -------------------------------------------

interface ProjectFileResult {
  ok?: boolean;
  id?: string;
  title?: string;
  type?: string;
  project?: string;
  mime?: string;
  sizeBytes?: number;
  content?: string;
  binary?: boolean;
  updatedAt?: string;
  createdAt?: string;
  error?: string;
}

export function buildProjectFileActionCard(
  tool: string,
  result: unknown,
): HTMLElement {
  const r = unwrapResult(result) as unknown as ProjectFileResult;
  const el = baueKarte(tool.replace(/_/g, "-"));
  const action = tool.replace("project_file_", "");
  const headLabel = (() => {
    switch (action) {
      case "create":
        return "Datei angelegt";
      case "update":
        return "Datei aktualisiert";
      case "delete":
        return "Datei gelöscht";
      case "get":
        return "Datei geöffnet";
      default:
        return tool;
    }
  })();
  el.appendChild(kartenTitel(headLabel));
  if (r.error) {
        el.appendChild(absatz(r.error));
    return el;
  }
  if (r.title) wertZeile(el, "Titel", r.title);
  if (r.type) wertZeile(el, "Typ", r.type);
  if (r.project) wertZeile(el, "Projekt", r.project);
  if (typeof r.sizeBytes === "number") {
    const kb =
      r.sizeBytes < 1024
        ? `${r.sizeBytes} B`
        : `${(r.sizeBytes / 1024).toFixed(1)} kB`;
    wertZeile(el, "Größe", kb);
  }
  // Bei get + textuellem Inhalt: kompakte Vorschau (max 12 Zeilen)
  if (action === "get" && typeof r.content === "string" && !r.binary) {
    const preview = r.content.split("\n").slice(0, 12).join("\n").slice(0, 400);
    if (preview) {
      const pre = document.createElement("pre");
      pre.className = "tool-file-preview";
      pre.textContent = preview;
      el.appendChild(pre);
      if (r.content.length > preview.length) {
        leerHinweis(el, "(gekürzt — siehe Projekt-Tab)");
      }
    }
  }
  return el;
}

interface NarrateResult {
  ok?: boolean;
  id?: string;
  title?: string;
  project?: string;
  project_id?: string;
  provider?: string;
  voice?: string;
  sizeBytes?: number;
  path?: string;
  error?: string;
}

export function buildNarrateCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as unknown as NarrateResult;
  const el = baueKarte("narrate-to-mp3");
  el.appendChild(kartenTitel("MP3 erzeugt"));
  if (r.error) {
        el.appendChild(absatz(r.error));
    return el;
  }
  if (r.title) wertZeile(el, "Titel", r.title);
  if (r.project) wertZeile(el, "Projekt", r.project);
  if (r.provider) {
    const providerLabel =
      r.provider === "elevenlabs" ? "ElevenLabs" : "Google Cloud TTS";
    wertZeile(el, "Stimme", `${providerLabel}${r.voice ? " · " + r.voice : ""}`);
  }
  if (typeof r.sizeBytes === "number") {
    const kb =
      r.sizeBytes < 1024
        ? `${r.sizeBytes} B`
        : `${(r.sizeBytes / 1024).toFixed(1)} kB`;
    wertZeile(el, "Größe", kb);
  }
  // Inline-Audio-Player. Wir bauen die URL aus path-Suffix —
  // path ist absolut (/opt/...), daraus mappen wir auf den raw-Endpoint.
  if (r.id && (r.project_id || r.project)) {
    // 2026-07-13 (Martin): Vesperas einheitlicher .player-bar-Player (nicht der
    // graue Browser-<audio>). Sofort sichtbar, wenn project_id direkt aus dem
    // Tool kommt; sonst Name → id auflösen (Alt-Karten in der History).
    const fileId = r.id;
    const mountPlayer = (projectId: string): void => {
      const url = `/api/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}/raw`;
      el.appendChild(buildAudioPlayer(url));
    };
    if (r.project_id) {
      mountPlayer(r.project_id);
    } else if (r.project) {
      void resolveProjectIdByName(r.project).then((projectId) => {
        if (projectId) mountPlayer(projectId);
      });
    }
    const hint = document.createElement("div");
    hint.className = "tool-narrate-hint";
    hint.textContent = 'Die Datei liegt auch im Projekt (Tab "Projekte").';
    el.appendChild(hint);
  }
  return el;
}

// Pre.366: Single-Flight-Cache. Bei Reload mit vielen narrate_to_mp3-Cards
// in der History wurde /api/projects vorher 4× parallel gefetcht — sichtbar
// als 4× 100-400ms Hänger im Network-Panel. Jetzt eine in-flight Promise,
// 30s Cache. Invalidate über window-Event 'voice-v8:projects-invalidate'.
let projectsCache: Promise<Array<{ id: string; name: string }>> | null = null;
let projectsCacheTs = 0;
const PROJECTS_CACHE_TTL_MS = 30_000;
window.addEventListener("voice-v8:projects-invalidate", () => {
  projectsCache = null;
});

async function resolveProjectIdByName(name: string): Promise<string | null> {
  if (!projectsCache || Date.now() - projectsCacheTs > PROJECTS_CACHE_TTL_MS) {
    projectsCacheTs = Date.now();
    projectsCache = fetch("/api/projects", { credentials: "same-origin" })
      .then(async (res) => {
        if (!res.ok) return [];
        const data = (await res.json()) as {
          items: Array<{ id: string; name: string }>;
        };
        return data.items;
      })
      .catch(() => []);
  }
  const items = await projectsCache;
  return items.find((p) => p.name === name)?.id ?? null;
}
